import React, { useEffect, useState, useRef } from 'react';
import Header from './common/Header';
import Footer from './common/Footer';
import LoginPopup from './popup/LoginPopup';
import SEO from '../components/common/SEO';
import './YatraDetails.css';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { getValidSession } from './helpers/sessionUtils';

import { FaPhoneAlt } from "react-icons/fa";
import { MdEmail } from "react-icons/md";

import { formatDateOnly } from '../utils/dateUtils';
import ListingVerifiedBadge from '../components/common/ListingVerifiedBadge';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
const CONTACT_NUMBER = process.env.REACT_APP_CONTACT_NUMBER || '9699480470';
const EMAIL_US = process.env.REACT_APP_EMAIL_US || 'help@mangoloops.com';

function YatraDetails() {
  const { slug } = useParams();

  // Extract ID from the end of the slug: e.g. "3-days-ujjain-ujjain-9999-3" → "3"
  let id;
  if (slug) {
    const lastHyphen = slug.lastIndexOf('-');
    const candidate = lastHyphen >= 0 ? slug.substring(lastHyphen + 1) : slug;
    if (/^\d+$/.test(candidate)) {
      id = candidate;
    } else {
      // Backward compat: try base64 decode or use as-is
      try { id = atob(slug); } catch (e) { id = slug; }
    }
  }

  const [showLoginPopup, setShowLoginPopup] = useState(false);

  const [otherYatras, setOtherYatras] = useState([]);
  const [otherYatrasLoading, setOtherYatrasLoading] = useState(true);
  const navigate = useNavigate();
  const [usersMap, setUsersMap] = useState({});

  const [pkg, setPkg] = useState(null);
  const [images, setImages] = useState([]);
  const [mainImage, setMainImage] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Always show exactly 5 thumbnails below the main image
  const displayImages = images.length > 0
    ? Array(5).fill(null).map((_, i) => images[i % images.length])
    : [];

  const [availableHotels, setAvailableHotels] = useState([]);
  const [hotelsLoading, setHotelsLoading] = useState(false);
  const [hotelReviewsCache, setHotelReviewsCache] = useState({});

  const [reviewsSummary, setReviewsSummary] = useState({ count: 0, avgRating: 0 });
  const [reviewsList, setReviewsList] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsModalOpen, setReviewsModalOpen] = useState(false);
  const [sortOption, setSortOption] = useState('most_relevant');
  const [pendingReviewOpen, setPendingReviewOpen] = useState(false);

  const [showWriteForm, setShowWriteForm] = useState(false);
  const [newRating, setNewRating] = useState(5);
  const [newReviewText, setNewReviewText] = useState('');

  const [currentUser, setCurrentUser] = useState(() => {
    const session = getValidSession();
    return session?.user?.user || session?.user || null;
  });
  const [currentUserReview, setCurrentUserReview] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const [showQueryForm, setShowQueryForm] = useState(false);
  const [queryFormData, setQueryFormData] = useState({ message: '', email: '', phone: '', numberOfPeople: '' });
  const [isSubmittingQuery, setIsSubmittingQuery] = useState(false);
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const mobileGalleryRef = useRef(null);
  const yatraTabsRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowStickyBar(true);
      } else {
        setShowStickyBar(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (window.innerWidth > 768) return;

    const tabsEl = yatraTabsRef.current;
    const activeButton = tabsEl?.querySelector('.tour-details-nav-btn.active');
    if (!tabsEl || !activeButton) return;

    tabsEl.scrollTo({
      left: activeButton.offsetLeft - (tabsEl.clientWidth - activeButton.clientWidth) / 2,
      behavior: 'smooth',
    });
  }, [activeTab]);

  // Auto-slide main image on mobile view (changes image every 3 seconds)
  useEffect(() => {
    if (displayImages.length <= 1) return;

    const interval = setInterval(() => {
      if (window.innerWidth <= 991) {
        setActiveIndex((currentIdx) => {
          const nextIdx = (currentIdx + 1) % displayImages.length;
          setMainImage(displayImages[nextIdx]);
          return nextIdx;
        });
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [displayImages]);

  // Smoothly scroll the active thumbnail into center view on mobile scrollbar
  useEffect(() => {
    if (window.innerWidth <= 991 && mobileGalleryRef.current) {
      const galleryEl = mobileGalleryRef.current;
      const activeImg = mobileGalleryRef.current.children[activeIndex];
      if (activeImg) {
        galleryEl.scrollTo({
          left: activeImg.offsetLeft - (galleryEl.clientWidth - activeImg.clientWidth) / 2,
          behavior: 'smooth',
        });
      }
    }
  }, [activeIndex]);

  const handleLoginSuccess = (loggedInUser) => {
    const session = getValidSession();
    const resolvedUser = session?.user?.user || session?.user || loggedInUser;
    setCurrentUser(resolvedUser);
    setShowLoginPopup(false);

    if (pendingAction) {
      processLead(pendingAction, resolvedUser);
    } else if (pendingReviewOpen) {
      setPendingReviewOpen(false);
      setReviewsModalOpen(true);
      setShowWriteForm(false);
      fetchReviews();
    } else {
      if (reviewsModalOpen) {
        fetchReviews();
        setShowWriteForm(false);
      }
    }
  };

  const token = localStorage.getItem('authToken') || localStorage.getItem('token') || null;
  useEffect(() => {
    if (token && !currentUser) {
      try {
        const decoded = jwtDecode(token);
        setCurrentUser({ 
          id: decoded.userId || decoded.id,
          name: decoded.userName || decoded.name || 'Guest',
          phone: decoded.phone || decoded.mobile || '',
          email: decoded.email || ''
        });
      } catch (error) {
        console.error("Failed to decode token:", error);
        setCurrentUser(null);
      }
    }
  }, [token]);

  // Activity Lead Recording
  useEffect(() => {
    if (currentUser && pkg && pkg.id && !sessionStorage.getItem(`activity_lead_yatra_${pkg.id}`)) {
      const sendActivityLead = async () => {
        try {
          const currentToken = localStorage.getItem('authToken') || localStorage.getItem('token');
          const headers = currentToken ? { Authorization: `Bearer ${currentToken}` } : {};
          
          await axios.post(`${API_BASE_URL}/enquiries/activity`, {
            type: 'tirth_yatra',
            type_id: pkg.id,
            mobile: currentUser.phone || currentUser.mobile || '',
            name: currentUser.name || 'Guest',
            email: currentUser.email || '',
            city: pkg.city || ''
          }, { headers });
          
          sessionStorage.setItem(`activity_lead_yatra_${pkg.id}`, 'true');
        } catch (e) {
          console.error("Activity lead error:", e);
        }
      };
      
      if (currentUser.phone || currentUser.mobile) {
        sendActivityLead();
      }
    }
  }, [currentUser, pkg]);

  const safeParse = (val) => {
    if (val === null || val === undefined) return null;
    if (typeof val === 'string') {
      const trimmed = val.trim();
      try {
        return JSON.parse(trimmed);
      } catch {
        if (trimmed.includes(',')) {
          return trimmed.split(',').map(s => s.trim()).filter(Boolean);
        }
        return trimmed;
      }
    }
    return val;
  };

  const parseHotelIds = (raw) => {
    if (!raw && raw !== 0) return [];
    if (Array.isArray(raw)) {
      return raw.map(x => Number(x)).filter(x => !Number.isNaN(x));
    }
    if (typeof raw === 'object') {
      try {
        const vals = Object.values(raw);
        return vals.map(x => Number(x)).filter(x => !Number.isNaN(x));
      } catch { }
    }
    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed.map(x => Number(x)).filter(x => !Number.isNaN(x));
      } catch { }
      if (trimmed.includes(',')) {
        return trimmed.split(',').map(s => Number(s.trim())).filter(x => !Number.isNaN(x));
      }
      const n = Number(trimmed);
      return Number.isNaN(n) ? [] : [n];
    }

    const n = Number(raw);
    return Number.isNaN(n) ? [] : [n];
  };

  // fetch yatra + hotels
  useEffect(() => {
    if (!id) {
      setError('Yatra ID missing');
      setLoading(false);
      return;
    }

    const fetchYatra = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_BASE_URL}/yatras/public/${id}`);
        // backend returns { yatra: { ... } } per your earlier code
        const data = res.data?.yatra || res.data || null;

        if (!data) {
          setError('Yatra not found');
          setLoading(false);
          return;
        }

        setPkg(data);

        // Handle images (your API returns pkg.images or pkg.Images)
        if (Array.isArray(data.images) && data.images.length > 0) {
          setImages(data.images);
          setMainImage(data.images[0]);
        } else if (Array.isArray(data.Images) && data.Images.length > 0) {
          const urls = data.Images.map((i) => i.image_url).filter(Boolean);
          setImages(urls);
          setMainImage(urls[0] || '');
        } else {
          setImages([]);
          setMainImage('');
        }

        const rawHotelIds = data.hotel_ids ?? data.hotel_ids ?? null;
        const parsedIds = parseHotelIds(rawHotelIds);

        if (parsedIds.length > 0) {
          setHotelsLoading(true);
          try {
            const hotelsRes = await axios.post(`${API_BASE_URL}/hotels/by-ids`, { ids: parsedIds });
            const hotelsData = hotelsRes.data || [];
            setAvailableHotels(Array.isArray(hotelsData) ? hotelsData : []);

            // Fetch reviews for each hotel
            if (Array.isArray(hotelsData)) {
              const newCache = {};
              for (const hotel of hotelsData) {
                if (hotel.id) {
                  try {
                    const reviewRes = await axios.get(`${API_BASE_URL}/hotels/${hotel.id}/reviews?summary=true`);
                    const body = reviewRes.data || {};
                    newCache[hotel.id] = {
                      avgRating: Number(body.avgRating || body.average_rating || body.avg_rating || 0),
                      count: Number(body.count || body.total_reviews || body.reviewCount || 0)
                    };
                  } catch (reviewErr) {
                    console.error(`Error fetching reviews for hotel ${hotel.id}:`, reviewErr);
                    newCache[hotel.id] = { avgRating: 0, count: 0 };
                  }
                }
              }
              setHotelReviewsCache(newCache);
            }
          } catch (hErr) {
            console.error('Error fetching hotels by ids', hErr);
            setAvailableHotels([]);
          } finally {
            setHotelsLoading(false);
          }
        } else {
          setAvailableHotels([]);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching yatra:', err);
        setError('Failed to load yatra details');
        setLoading(false);
      }
    };

    fetchYatra();
  }, [id]);
  useEffect(() => {
    if (id) {
      const fetchOtherYatras = async () => {
        setOtherYatrasLoading(true);
        try {
          const res = await axios.get(`${API_BASE_URL}/yatras/${id}/other-yatras?limit=5`);
          setOtherYatras(res.data.yatras || []);
        } catch (error) {
          console.error("Error fetching other yatras:", error);
          setOtherYatras([]);
        } finally {
          setOtherYatrasLoading(false);
        }
      };
      fetchOtherYatras();
    }
  }, [id]);

  const parseReviewsBody = (body) => {
    if (!body) return [];
    if (Array.isArray(body)) return body;
    if (Array.isArray(body.reviews)) return body.reviews;
    if (Array.isArray(body.data?.reviews)) return body.data.reviews;
    if (Array.isArray(body.data)) return body.data;
    if (Array.isArray(body.items)) return body.items;
    const arrKey = Object.keys(body).find((k) => Array.isArray(body[k]));
    if (arrKey) return body[arrKey];
    return [];
  };


  const fetchReviews = async () => {
    setReviewsLoading(true);
    setCurrentUserReview(null);
    try {
      const keyToken = localStorage.getItem('authToken') || localStorage.getItem('token');
      const headers = keyToken ? { Authorization: `Bearer ${keyToken}` } : {};

      const url = `${API_BASE_URL}/yatras/${id}/reviews`;
      const res = await axios.get(url, { headers });
      const body = res.data ?? res;
      const reviews = parseReviewsBody(body);

      if (typeof body.count !== 'undefined' || typeof body.avgRating !== 'undefined') {
        setReviewsSummary({
          count: Number(body.count) || 0,
          avgRating: Number(body.avgRating) || 0
        });
      } else if (body.summary) {
        setReviewsSummary({
          count: Number(body.summary.count) || 0,
          avgRating: Number(body.summary.avgRating) || 0
        });
      }

      const userIds = Array.from(new Set(reviews.map((r) => r.user_id || r.userId || r.user?.id || r.User?.id).filter(Boolean)));
      const missingIds = userIds.filter((uid) => {
        const reviewWithUser = reviews.find((r) => r.User && r.User.id === uid);
        return !reviewWithUser && !usersMap[uid];
      });

      if (missingIds.length > 0) {
        const newUsersMap = { ...usersMap };
        try {
          const batchUrl = `${API_BASE_URL}/users?ids=${missingIds.join(',')}`;
          const batchRes = await axios.get(batchUrl, { headers });
          let fetchedUsers = [];
          if (Array.isArray(batchRes.data)) fetchedUsers = batchRes.data;
          else if (Array.isArray(batchRes.data.users)) fetchedUsers = batchRes.data.users;
          else if (Array.isArray(batchRes.data.data)) fetchedUsers = batchRes.data.data;
          fetchedUsers.forEach((u) => {
            if (u.id) newUsersMap[u.id] = u;
          });
          setUsersMap(newUsersMap);
        } catch (batchErr) {
          console.warn('Batch user fetch failed, falling back to individual fetches.', batchErr);
        }
      }

      setReviewsList(reviews || []);

      if (currentUser && reviews.length > 0) {
        const foundReview = reviews.find(r => String(r.user_id || r.userId || r.user?.id || r.User?.id) === String(currentUser.id));
        if (foundReview) {
          setCurrentUserReview(foundReview);
        }
      }
    } catch (err) {
      console.error('fetchReviews -> error', err);
      setReviewsList([]);
    } finally {
      setReviewsLoading(false);
    }
  };

  const openReviewsModal = async () => {
    if (!currentUser && !token) {
      setPendingReviewOpen(true);
      setShowLoginPopup(true);
      return;
    }

    setReviewsModalOpen(true);
    setShowWriteForm(false);
    await fetchReviews();
  };


  const handleWriteClick = () => {
    if (!currentUser) {
      setReviewsModalOpen(false);
      setShowWriteForm(false);
      setPendingReviewOpen(true);
      setShowLoginPopup(true);
      return;
    }

    if (currentUserReview) {
      setNewRating(currentUserReview.rating);
      setNewReviewText(currentUserReview.review_text || '');
      setIsEditing(true);
    } else {
      setNewRating(5);
      setNewReviewText('');
      setIsEditing(false);
    }
    setShowWriteForm(true);
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!newRating || newRating < 1 || newRating > 5) {
      alert('Please choose a rating between 1 and 5.');
      return;
    }

    const reviewData = { rating: newRating, review_text: newReviewText };
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

    try {
      let res;
      if (isEditing && currentUserReview) {
        // CORRECTED LINE: Use the hotel ID (`id`) from useParams
        const updateUrl = `${API_BASE_URL}/yatras/${id}/reviews`;
        res = await axios.put(updateUrl, reviewData, { headers });
      } else {
        const createUrl = `${API_BASE_URL}/yatras/${id}/reviews`;
        res = await axios.post(createUrl, reviewData, { headers });
      }

      if (res.status === 200 || res.status === 201) {
        await fetchReviews();
        // await fetchReviewSummary(); // <-- PROBLEM LINE
        setShowWriteForm(false);
        setIsEditing(false);
      } else {
        alert(res.data?.message || 'Unable to submit review.');
      }
    } catch (err) {
      console.error('handleSubmitReview -> error', err);
      alert(err.response?.data?.message || 'Network/server error while submitting review.');
    }
  };

  const handleAction = (e, actionType) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (isSubmittingQuery) return;

    setPendingAction(actionType);
    setIsSubmittingQuery(true);

    if (!currentUser) {
      setShowLoginPopup(true);
      return;
    }
    processLead(actionType, currentUser);
  };

  const processLead = async (actionType, userData = null) => {
    setIsSubmittingQuery(true);
    const sessionUser = userData || currentUser;

    const payload = {
      name: sessionUser?.name || 'Guest',
      email: sessionUser?.email || '',
      mobile: (sessionUser?.phone || sessionUser?.mobile || '').replace(/^(\+91|91)/, ''),
      type: 'yatra',
      type_id: id,
      message: `User clicked ${actionType} for Yatra Package: ${yatraName}`,
    };

    let success = false;
    try {
      const currentToken = localStorage.getItem('authToken') || localStorage.getItem('token');
      const headers = currentToken ? { Authorization: `Bearer ${currentToken}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
      const response = await axios.post(`${API_BASE_URL}/enquiries/create`, payload, { headers });

      if (response.data.success || response.status === 200 || response.status === 201) {
        success = true;
        setShowThankYouModal(true);
      }
    } catch (err) {
      console.error('Lead submission error:', err);
      alert(err.response?.data?.message || 'Failed to process request. Please try again.');
    } finally {
      setIsSubmittingQuery(false);
      if (!success) {
        setPendingAction(null);
      }
    }
  };

  const handleFinalRedirect = (actionType) => {
    setShowThankYouModal(false);
    const contactNumber = pkg?.Vendor?.phone || pkg?.Vendor?.whatsapp_no;
    const userName = currentUser?.name || 'Guest';
    const userPhone = currentUser?.phone || currentUser?.mobile || '';
    const userEmail = currentUser?.email || '';
    const whatsappMsg = encodeURIComponent(
      `Hi, I am ${userName} (Phone: ${userPhone}, Email: ${userEmail}). I am interested in ${yatraName}. Please share more details.`
    );

    if (actionType === 'call' && contactNumber) {
      window.location.href = `tel:${contactNumber}`;
    } else if (actionType === 'whatsapp' && contactNumber) {
      window.open(`https://wa.me/${contactNumber}?text=${whatsappMsg}`, '_blank');
    } else if (actionType === 'book') {
      // Just stay or show confirmation info
    }
    setPendingAction(null);
  };

  const changeImage = (img, idx) => {
    setMainImage(img);
    if (idx !== undefined) {
      setActiveIndex(idx);
    }
  };
  useEffect(() => {
    const fetchReviewSummaryOnLoad = async () => {
      if (!id) return;
      try {
        const res = await axios.get(`${API_BASE_URL}/yatras/${id}/reviews?summary=true`);
        const body = res.data || {};
        if (typeof body.count !== 'undefined' || typeof body.avgRating !== 'undefined') {
          setReviewsSummary({ count: Number(body.count) || 0, avgRating: Number(body.avgRating) || 0 });
        } else if (body.data) {
          setReviewsSummary({ count: Number(body.data.count) || 0, avgRating: Number(body.data.avgRating) || 0 });
        }
      } catch (err) {
        console.error('Error fetching review summary on load:', err);
      }
    };

    fetchReviewSummaryOnLoad();
  }, [id]);

  const renderStarsHoliday = (rating, size = 16) => {
    console.log(rating);
    const stars = [];
    const full = Math.floor(rating || 0);
    const half = (rating || 0) - full >= 0.5;
    for (let i = 1; i <= 5; i++) {
      if (i <= full) {
        stars.push(<i key={i} className="fas fa-star" style={{ color: '#f4b400', fontSize: size, marginRight: 4 }} />);
      } else if (i === full + 1 && half) {
        stars.push(<i key={i} className="fas fa-star-half-alt" style={{ color: '#f4b400', fontSize: size, marginRight: 4 }} />);
      } else {
        stars.push(<i key={i} className="far fa-star" style={{ color: '#f4b400', fontSize: size, marginRight: 4, opacity: 0.5 }} />);
      }
    }
    return <span style={{ display: 'inline-flex', alignItems: 'center' }}>{stars}</span>;
  };

  // Fetch hotel reviews to get accurate rating and review count
  const fetchHotelReviews = async (hotelId) => {
    if (hotelReviewsCache[hotelId]) {
      return hotelReviewsCache[hotelId];
    }

    try {
      const res = await axios.get(`${API_BASE_URL}/hotels/${hotelId}/reviews?summary=true`);
      const body = res.data || {};
      const reviewData = {
        avgRating: Number(body.avgRating || body.average_rating || body.avg_rating || 0),
        count: Number(body.count || body.total_reviews || body.reviewCount || 0)
      };

      setHotelReviewsCache(prev => ({
        ...prev,
        [hotelId]: reviewData
      }));

      return reviewData;
    } catch (err) {
      console.error('Error fetching hotel reviews:', err);
      return { avgRating: 0, count: 0 };
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <main className="container py-5 text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading yatra details...</p>
        </main>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <main className="container py-5 text-center">
          <div className="alert alert-danger">{error}</div>
          <button className="btn btn-secondary" onClick={() => navigate(-1)}>
            Go Back
          </button>
        </main>
        <Footer />
      </>
    );
  }

  // Extract fields (keep same names as before)
  const yatraName = pkg?.yatra_name || 'Holiday Yatra';
  const yatra_city = pkg?.city || '';
  const price = pkg?.yatra_price || '';
  const offerPrice = pkg?.offer_price || '';
  const nights = pkg?.nights || '';
  const description = pkg?.description || '';
  const days = pkg?.days || '';
  const overview = pkg?.yatra_overview || pkg?.description || '';
  const itinerary = safeParse(pkg?.itinerary) || [];
  const howToReach = pkg?.how_to_reach || '';
  const placesToVisit = safeParse(pkg?.places_to_visit) || [];

  // displayImages declared above

  return (
    <div className="yatra-detail-page">
      <SEO title={`${yatraName} | Mangoloops`} description={overview.substring(0, 160)} />
      <Header />
      <LoginPopup />

      <main id="content">
        <div className="banner-content container pt-3 holidays-left">
          <div className="row pb-5">
            <div className="col-lg-12 pb-3">
              <div className="row">
                <div className="mflex acenter justify-content-between yatra-detail-title-row">
                  <div className="yatra-detail-title-stack">
                    <span className="pkgname ng-binding">{yatraName}</span>
                    <span className="pkgtimex yatra-detail-duration ng-binding">{nights ? `${nights} Nights` : ''} {days ? `/ ${days} Days` : ''}</span>
                  </div>
                  <div className="desktop-only ml-3 text-right" role="button" tabIndex={0} onClick={openReviewsModal} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openReviewsModal(); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', flex: '0 0 auto', whiteSpace: 'nowrap' }}>
                    {renderStarsHoliday(reviewsSummary.avgRating, 18)}
                    <span className="rating-text" style={{ marginLeft: 4, fontWeight: 600 }}>{Number(reviewsSummary.avgRating || 0).toFixed(1)}/5</span>
                    <span className="rating-text" style={{ marginLeft: 8, color: '#666' }}>({reviewsSummary.count || 0} {reviewsSummary.count === 1 ? 'review' : 'reviews'})</span>
                  </div>
                </div>
                <div className="stycitydtl ng-binding">
                  {description?.length > 200 ? `${description.substring(0, 200)}...` : description}
                </div>
              </div>
            </div>

            <div className="col-lg-8 mb-5">
              <div className="tour-yatra-container">
                {/* Main Image */}
                <div className="tour-yatra-image yatra-main-photo-wrap">
                  <ListingVerifiedBadge item={pkg} detail />
                  <img
                    src={mainImage || images[0] || '/no_image_teerth-yatra.jpg'}
                    alt="Yatra"
                    style={{ width: '100%', borderRadius: 8 }}
                  />
                  <button
                    type="button"
                    className="detail-photo-review-badge"
                    onClick={openReviewsModal}
                    aria-label="Open yatra reviews"
                  >
                    <span className="detail-photo-review-stars">
                      {renderStarsHoliday(reviewsSummary.avgRating || 0, 15)}
                    </span>
                    <span className="detail-photo-review-score">
                      {Number(reviewsSummary.avgRating || 0).toFixed(1)}/5
                    </span>
                    <span className="detail-photo-review-count">
                      ({reviewsSummary.count || 0} {reviewsSummary.count === 1 ? 'review' : 'reviews'})
                    </span>
                  </button>
                </div>

                {/* Desktop Gallery (exactly 5 thumbnails) */}
                {displayImages.length > 0 && (
                  <div className="image-gallery desktop-only d-flex mt-2" style={{ gap: '13px' }}>
                    {displayImages.map((img, idx) => (
                      <img
                        key={idx}
                        src={img}
                        alt={`thumb-${idx}`}
                        onClick={() => changeImage(img, idx)}
                        style={{
                          flex: 1,
                          minWidth: 0,
                          height: 90,
                          cursor: 'pointer',
                          border: idx === activeIndex ? '2px solid #007bff' : '1px solid #ddd',
                          objectFit: 'cover',
                          borderRadius: 4,
                        }}
                      />
                    ))}
                  </div>
                )}

                {/* Mobile Gallery (scrollable, showing 2 at a time, auto & manual) */}
                {displayImages.length > 0 && (
                  <div ref={mobileGalleryRef} className="image-gallery-mobile mobile-only mt-2">
                    {displayImages.map((img, idx) => (
                      <img
                        key={idx}
                        src={img}
                        alt={`thumb-${idx}`}
                        onClick={() => changeImage(img, idx)}
                        className={idx === activeIndex ? 'active' : ''}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Additional Content */}
              <div className="row pt-3 holidays-left">
                <div className="nav-container">
                  <ul className="nav nav-pills gap-18 mb-1 yatra-detail-tabs-scroll" id="pills-tab" role="tablist" ref={yatraTabsRef}>
                    <li className="nav-item" role="presentation">
                      <button className={`nav-link tour-details-nav-btn ${activeTab === 'overview' ? 'active' : ''}`}
                        onClick={() => setActiveTab('overview')}
                        id="pills-overview-tab"
                        data-bs-toggle="pill"
                        data-bs-target="#pills-overview"
                        type="button"
                        role="tab"
                        aria-controls="pills-overview"
                        aria-selected="true">
                        Overview
                      </button>
                    </li>

                    <li className="nav-item" role="presentation">
                      <button className={`nav-link tour-details-nav-btn ${activeTab === 'itinerary' ? 'active' : ''}`}
                        onClick={() => setActiveTab('itinerary')}
                        id="pills-itinerary-tab"
                        data-bs-toggle="pill"
                        data-bs-target="#pills-itinerary"
                        type="button"
                        role="tab"
                        aria-controls="pills-itinerary"
                        aria-selected="true">
                        Itinerary
                      </button>
                    </li>

                    <li className="nav-item" role="presentation">
                      <button className={`nav-link tour-details-nav-btn ${activeTab === 'hotel' ? 'active' : ''}`}
                        onClick={() => setActiveTab('hotel')}
                        id="pills-profile-tab"
                        data-bs-toggle="pill"
                        data-bs-target="#pills-profile"
                        type="button"
                        role="tab"
                        aria-controls="pills-profile"
                        aria-selected="false">
                        Available Hotel
                      </button>
                    </li>

                    <li className="nav-item" role="presentation">
                      <button className={`nav-link tour-details-nav-btn ${activeTab === 'contact' ? 'active' : ''}`}
                        onClick={() => setActiveTab('contact')}
                        id="pills-contact-tab"
                        data-bs-toggle="pill"
                        data-bs-target="#pills-contact"
                        type="button"
                        role="tab"
                        aria-controls="pills-contact"
                        aria-selected="false">
                        How to Reach
                      </button>
                    </li>
                  </ul>
                </div>


                <div className="tab-content" id="pills-tabContent">

                  {activeTab === 'overview' && (
                    <div className="tab-pane fade show mb-4 active" id="pills-overview" role="tabpanel" aria-labelledby="pills-overview-tab" tabIndex="0">
                      <div className="">
                        <div className="boxv1">
                          <h5 className="tour-details-h5">Yatra Overview</h5>

                          <div
                            className="description-p"
                            dangerouslySetInnerHTML={{ __html: overview }}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'itinerary' && (
                    <div className="tab-pane fade show mb-4 active" id="pills-itinerary" role="tabpanel" aria-labelledby="pills-itinerary-tab" tabIndex="0">

                      <div className="boxv1">

                        <div className="wrapper">

                          <h5 className="tour-details-h5">Day Wise Itinerary</h5>

                          {Array.isArray(itinerary) && itinerary.length > 0 ? (
                            <ul className="sessions">
                              {itinerary.map((it, i) => (
                                <li className="session-li" key={i}>
                                  <div className="itinerary-sec">
                                    <div className="itinerary-date">{it.title || `Day ${i + 1}`}</div>
                                    <div className="itinerary-para">
                                      <p className="itinerary-p">{it.description || it.desc || ''}</p>
                                    </div>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p>No itinerary available.</p>
                          )}

                        </div>

                      </div>

                    </div>
                  )}

                  {activeTab === 'hotel' && (
                    <div className="tab-pane fade  show mb-5 mt-5 active" id="pills-profile" role="tabpanel" aria-labelledby="pills-profile-tab" tabIndex="0">
                      <div className="row">
                        <div className="hotel-main-sec">
                          <div className="available-hotel row">
                            {/* Render hotels dynamically here */}
                            {hotelsLoading ? (
                              <div className="col-12 text-center p-4">
                                <div className="spinner-border text-primary" role="status"></div>
                              </div>
                            ) : (availableHotels && availableHotels.length > 0 ? (
                              availableHotels.map((hotel, idx) => {
                                const hotelImageFromGallery = Array.isArray(hotel.Images)
                                  ? (
                                    hotel.Images.find((img) => img.image_size_name === 'large' || img.image_url?.includes('_large.')) ||
                                    hotel.Images.find((img) => !img.image_url?.includes('_thumbnail.')) ||
                                    hotel.Images[0]
                                  )?.image_url
                                  : null;
                                const hotelImage = hotelImageFromGallery || hotel.image || hotel.image_url || '/no_image_hotel.jpg';

                                // Get hotel reviews from cache
                                const hotelReviewData = hotelReviewsCache[hotel.id] || { avgRating: 0, count: 0 };
                                const hotelRating = hotelReviewData.avgRating || hotel.rating || hotel.avg_rating || 0;
                                const reviewCount = hotelReviewData.count || hotel.reviews_count || hotel.review_count || hotel.reviewCount || 0;
                                const ratingLabel = hotelRating >= 4.5 ? 'Excellent' : hotelRating >= 4 ? 'Very Good' : hotelRating >= 3.5 ? 'Good' : hotelRating > 0 ? 'Good' : 'No Ratings';

                                const renderHotelStars = (rating, size = 14) => {
                                  const stars = [];
                                  const full = Math.floor(rating || 0);
                                  const half = (rating || 0) - full >= 0.5;
                                  for (let i = 1; i <= 5; i++) {
                                    if (i <= full) {
                                      stars.push(<i key={i} className="fas fa-star" style={{ color: '#FFB81C', fontSize: size, marginRight: 2 }} />);
                                    } else if (i === full + 1 && half) {
                                      stars.push(<i key={i} className="fas fa-star-half-alt" style={{ color: '#FFB81C', fontSize: size, marginRight: 2 }} />);
                                    } else {
                                      stars.push(<i key={i} className="far fa-star" style={{ color: '#FFB81C', fontSize: size, marginRight: 2, opacity: 0.3 }} />);
                                    }
                                  }
                                  return stars;
                                };

                                const slugify = (str) =>
                                  str
                                    ? String(str)
                                      .toLowerCase()
                                      .replace(/\s+/g, '-')
                                      .replace(/[^\w\-]+/g, '')
                                      .replace(/\-\-+/g, '-')
                                      .replace(/^-+/, '')
                                      .replace(/-+$/, '')
                                    : '';

                                const generateHotelSlug = (hotelObj) => {
                                  const name = slugify(hotelObj?.hotel_name || hotelObj?.name);
                                  const city = slugify(hotelObj?.city || hotelObj?.location_city);
                                  const price = Math.round(hotelObj?.offer_price || hotelObj?.hotel_price || hotelObj?.price || 0);
                                  const idVal = hotelObj?.id;

                                  return [name, city, price ? price : null, idVal]
                                    .filter(Boolean)
                                    .join('-')
                                    .replace(/--+/g, '-');
                                };

                                return (
                                  <div className="col-12 mb-4" key={hotel.id || idx} style={{ transition: 'all 0.3s ease' }}>
                                    <div style={{
                                      background: '#fff',
                                      borderRadius: '12px',
                                      overflow: 'hidden',
                                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                      transition: 'all 0.3s ease',
                                      cursor: 'pointer',
                                      border: '1px solid #f0f0f0'
                                    }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.boxShadow = '0 8px 24px rgba(255,107,53,0.15)';
                                        e.currentTarget.style.transform = 'translateY(-4px)';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                      }}>
                                      <div className="row g-0">
                                        {/* Hotel Image Section */}
                                        <div className="col-md-4 col-lg-3 position-relative yatra-available-hotel-image-wrap">
                                          <img
                                            src={hotelImage}
                                            alt={hotel.hotel_name || 'Hotel'}
                                            className="yatra-available-hotel-image"
                                            onError={(e) => {
                                              e.currentTarget.onerror = null;
                                              e.currentTarget.src = '/no_image_hotel.jpg';
                                            }}
                                            style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            display: 'block'
                                          }}
                                          />
                                          <div style={{
                                            position: 'absolute',
                                            top: '10px',
                                            right: '10px',
                                            background: 'linear-gradient(135deg, #FFB81C 0%, #FF9800 100%)',
                                            color: '#fff',
                                            padding: '6px 12px',
                                            borderRadius: '20px',
                                            fontWeight: '600',
                                            fontSize: '12px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                                          }}>
                                            ★ {hotelRating.toFixed(1)}
                                          </div>
                                        </div>

                                        {/* Hotel Details Section */}
                                        <div className="col-md-5 col-lg-6 p-4 d-flex flex-column justify-content-between">
                                          <div>
                                            <h5 style={{
                                              fontSize: '18px',
                                              fontWeight: '700',
                                              color: '#333',
                                              marginBottom: '8px'
                                            }}>{hotel.hotel_name || hotel.name || 'Hotel'}</h5>

                                            <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'space-between', width: '100%' }}>
                                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                  {renderHotelStars(hotelRating, 14)}
                                                </div>
                                                <span style={{ fontSize: '13px', color: 'black', fontWeight: '600' }}>
                                                  {hotelRating.toFixed(1)}
                                                </span>
                                              </div>
                                              <span style={{ fontSize: '12px', color: 'black', fontWeight: '500' }}>
                                                {reviewCount > 0 ? `${reviewCount.toLocaleString()} reviews` : 'No reviews'}
                                              </span>
                                            </div>


                                            <div style={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: '6px',
                                              color: '#666',
                                              fontSize: '13px',
                                              marginBottom: '12px'
                                            }}>
                                              <i className="fa fa-map-marker-alt" style={{ color: 'black' }} />
                                              {hotel.city || hotel.location || 'Location'}
                                            </div>

                                            <div style={{
                                              display: 'flex',
                                              gap: '12px',
                                              flexWrap: 'wrap',
                                              marginTop: '10px'
                                            }}>
                                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#555' }}>
                                                <i className="fas fa-utensils" style={{ color: 'black', fontSize: '14px' }} /> Restaurant
                                              </div>
                                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#555' }}>
                                                <i className="fas fa-spa" style={{ color: 'black', fontSize: '14px' }} /> Dry Cleaning
                                              </div>
                                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#555' }}>
                                                <i className="fas fa-bell" style={{ color: 'black', fontSize: '14px' }} /> Wake-up Call
                                              </div>
                                            </div>
                                          </div>
                                        </div>

                                        {/* Price & CTA Section */}
                                        <div className="col-md-3 col-lg-3 p-4" style={{
                                          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                                          display: 'flex',
                                          flexDirection: 'column',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          borderLeft: '1px solid #f0f0f0'
                                        }}>
                                          <div style={{ textAlign: 'center', width: '100%' }}>
                                            <div style={{ fontSize: '12px', color: '#999', marginBottom: '6px', fontWeight: '500' }}>Price per Night</div>
                                            <div style={{
                                              fontSize: '24px',
                                              fontWeight: '700',
                                              color: '#128abe',
                                              marginBottom: '4px'
                                            }}>
                                              ₹{(hotel.offer_price || hotel.hotel_price || 0).toLocaleString()}
                                            </div>
                                            {hotel.hotel_price && hotel.offer_price && hotel.hotel_price > hotel.offer_price && (
                                              <div style={{
                                                fontSize: '11px',
                                                color: '#999',
                                                textDecoration: 'line-through'
                                              }}>
                                                ₹{hotel.hotel_price.toLocaleString()}
                                              </div>
                                            )}
                                          </div>

                                          <a
                                            href={`/hotel-details/${generateHotelSlug(hotel)}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                              background: 'linear-gradient(135deg, #128abe 0%, #0e6fa3 100%)',
                                              color: '#fff',
                                              border: 'none',
                                              padding: '10px 18px',
                                              borderRadius: '6px',
                                              fontWeight: '600',
                                              cursor: 'pointer',
                                              textDecoration: 'none',
                                              fontSize: '13px',
                                              width: '100%',
                                              textAlign: 'center',
                                              transition: 'all 0.3s ease',
                                              marginTop: '12px'
                                            }}
                                            onMouseEnter={(e) => {
                                              e.currentTarget.style.transform = 'scale(1.05)';
                                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(18,138,190,0.4)';
                                            }}
                                            onMouseLeave={(e) => {
                                              e.currentTarget.style.transform = 'scale(1)';
                                              e.currentTarget.style.boxShadow = 'none';
                                            }}
                                          >
                                            Book Now <i className="fas fa-arrow-right" style={{ marginLeft: '6px' }} />
                                          </a>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <div className="col-12 p-4 text-center text-muted">
                                No hotels available for this yatra.
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'contact' && (
                    <div className="tab-pane fade mt-3 show active" id="pills-contact" role="tabpanel" aria-labelledby="pills-contact-tab" tabIndex="0">
                      <div className="boxv1">
                        <h5 className="tour-details-h5">How to Reach</h5>

                        {/* Render dynamic how_to_reach (HTML) if present, otherwise show fallback list */}
                        {howToReach && String(howToReach).trim() ? (
                          <div dangerouslySetInnerHTML={{ __html: howToReach }} />
                        ) : (
                          <ul className="how-reach-ul">
                            <li className="list-style-circle how-to-reach-des">Himachal Pradesh is well-connected by air, making it convenient for travelers from across India and abroad. The major airports in the state include Bhuntar Airport (near Kullu-Manali), Gaggal Airport (near Dharamshala), and Jubbarhatti Airport (near Shimla). Regular flights operate from Delhi, Chandigarh, and other major cities to these airports. Upon landing, you can hire taxis or avail of pre-arranged transport services provided in your tour yatra to reach your destination.</li>
                            <li className="list-style-circle how-to-reach-des">Although Himachal Pradesh does not have a widespread railway network due to its mountainous terrain, there are charming narrow-gauge train routes that enhance the travel experience. The Kalka-Shimla toy train is a UNESCO World Heritage experience, offering scenic views and a relaxed journey to Shimla. The nearest major railheads to other parts of Himachal Pradesh are in Chandigarh and Pathankot. From there, you can opt for road transport to complete your journey.</li>
                            <li className="list-style-circle how-to-reach-des">Road connectivity is the most popular and flexible way to reach Himachal Pradesh. The state is well-connected to Delhi, Chandigarh, and nearby states through a network of national and state highways. You can opt for private taxis, self-driven cars, or luxury buses that operate daily to popular tourist destinations like Manali, Shimla, and Dharamshala. The picturesque views along the way make the road journey an adventure in itself.</li>
                            <li className="list-style-circle how-to-reach-des">Most Himachal Pradesh tour yatras include comprehensive transportation options, ensuring a hassle-free journey. Tour operators often provide door-to-door pick-up and drop-off services, covering flights, trains, and road transfers. Whether you’re traveling solo, with friends, or family, these yatras ensure a smooth journey to explore Himachal Pradesh’s breathtaking beauty without worrying about logistics.</li>
                          </ul>
                        )}
                      </div>
                    </div>
                  )}

                </div>

              </div>
            </div>

            {/* Sidebar */}
            <div className="col-lg-4 ">

              <aside className="sidebar-area sticky">
                <div className="mb-1">

                  <div className="pricecolm">
                    <div className="pda10 grbg bdrbtm">
                      <div className="mflex bg-color-btn">
                        <div className="price-sec">

                          <p className="strt">Starting from</p>

                          <p className="tlprice">
                            <span ng-bind="packDetails.currSymbol" className="ng-binding">₹ </span>
                            <span ng-bind="packDetails.twoPaxOccupancy" className="ng-binding"> {offerPrice ? Math.round(offerPrice) : 'Price as per request'}</span>
                            <small className="pptxt">Per Person</small>
                          </p>

                          <p className="famous-places-sidebar">Famous Attractions and Cultural Highlights of {yatra_city}</p>

                          <div className="stage" id="pricewaitd" style={{ display: 'none' }}>
                            <div className="dot-pulse"></div>
                          </div>

                          <div className="clr"></div>

                        </div>

                      </div>
                    </div>


                    <div className="pda13">
                      <div className="d-flex flex-column gap-2" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <button
                          className="sbmtbtn submit-query-btn yatra-detail-query-btn w-100"
                          id="myBt"
                          onClick={(e) => handleAction(e, "query")}
                          disabled={isSubmittingQuery}
                          style={{ cursor: 'pointer', margin: 0, padding: '12px 15px', borderRadius: '4px', fontSize: '16px', fontWeight: 'bold' }}
                        >
                          {isSubmittingQuery && pendingAction === "query" ? (
                            <span className="spinner-border spinner-border-sm" style={{ marginRight: '8px', display: 'inline-block', width: '1rem', height: '1rem', verticalAlign: 'text-bottom', border: '.2em solid currentColor', borderRightColor: 'transparent', borderRadius: '50%', animation: 'spinner-border .75s linear infinite' }}></span>
                          ) : null}
                          {isSubmittingQuery && pendingAction === "query" ? 'Submitting...' : 'Submit Your Query'}
                        </button>
                      </div>
                    </div>
                    <div className="clr"></div>

                  </div>

                  <div className="main-rg-hol-1 mgt15 mb-4">

                    <div className="man-sec-hol-1 places-to-visit-card">
                      <ul>
                        <li>
                          <span><i className="fa fa-clock pr-5px"></i></span>
                          <span className="details-p-icon"> Duration : </span>
                          <span className="ng-binding"> {nights ? `${nights} Nights` : 'N/A'} </span>
                        </li>

                        <li>
                          <div className="pb-2">
                            <span> <i className="fa fa-map-marked-alt pr-5px"></i></span>
                            <span className="details-p-icon"> Places to Visit : </span>
                          </div>
                          <span>
                            {/* placesToVisit may be an array or string */}
                            {Array.isArray(placesToVisit) ? placesToVisit.join(' | ') : (placesToVisit || 'N/A')}
                          </span>
                        </li>
                      </ul>
                    </div>

                  </div>

                  <div className="">
                    <div className="tour-yatras mt-4">
                      <h2 className="tour-packasge-sec-title">
                        {otherYatras.length > 0 && otherYatras.some(op => op.city && op.city.toLowerCase() === (yatra_city || '').toLowerCase())
                          ? `Other ${yatra_city} Yatras`
                          : 'Other Yatra Packages'}
                      </h2>
                      <div className="yatras">
                        {otherYatrasLoading ? (
                          <div className="text-center p-3">
                            <div className="spinner-border spinner-border-sm" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                          </div>
                        ) : otherYatras.length > 0 ? (
                          otherYatras.map((op) => (
                            <Link to={`/yatra-details/${[op.yatra_name, op.city, Math.round(op.price || op.offer_price || op.yatra_price), op.id].filter(Boolean).map(s => s.toString().toLowerCase().replace(/\s+/g, '-').replace(/[^\w\-]+/g, '').replace(/\-\-+/g, '-').replace(/^-+/, '').replace(/-+$/, '')).join('-')}`} className="yatra" key={op.id}>
                              <img
                                src={op.image || '/no_image_teerth-yatra.jpg'}
                                alt={op.yatra_name}
                              />
                              <div className="details">
                                <p>{op.yatra_name}</p>
                                <span>₹ {Math.round(op.price || op.offer_price || op.yatra_price)}</span>
                              </div>
                            </Link>
                          ))
                        ) : (
                          <p className="p-3 text-muted">No other yatras available.</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Need Help div removed completely */}


                </div>
              </aside>

            </div>
          </div>
        </div>

        <section>
          <div className="container"></div>
        </section>
      </main>

      {reviewsModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999 }} onClick={() => { setReviewsModalOpen(false); setShowWriteForm(false); }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', padding: 0, borderRadius: 8, width: 920, maxWidth: '96%', maxHeight: '88vh', overflowY: 'auto', position: 'relative', zIndex: 100000, boxShadow: '0 8px 30px rgba(0,0,0,0.3)' }}>
            <div style={{ padding: '18px 22px 0px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 22 }}>{yatraName} , {yatra_city}</h2>
                <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <strong style={{ fontSize: 20 }}>{Number(reviewsSummary.avgRating || 0).toFixed(1)}</strong>
                  <span>{renderStarsHoliday(reviewsSummary.avgRating || 0, 18)}</span>
                  <span style={{ color: '#666' }}>({reviewsSummary.count || 0} {reviewsSummary.count === 1 ? 'review' : 'reviews'})</span>
                </div>
                <div style={{ color: '#666', marginTop: 8, fontSize: 14 }}><i className="fa fa-map-marker-alt" /> {description} </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={handleWriteClick} style={{ padding: "8px 14px", borderRadius: "20px", border: "1px solid rgb(0, 123, 255)", background: "#fff", color: "rgb(0, 123, 255)", cursor: "pointer", whiteSpace: "nowrap" }}>
                  {currentUserReview ? 'Edit your review' : 'Write a review'}
                </button>
                <button onClick={() => { setReviewsModalOpen(false); setShowWriteForm(false); }} style={{ background: 'transparent', border: 'none', fontSize: 20, cursor: 'pointer' }}>×</button>
              </div>
            </div>
            <div style={{ padding: '12px 22px', borderBottom: '1px solid #f7f7f7' }}>
              <div style={{ marginBottom: 10, color: '#333' }}>Sort by</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {['most_relevant', 'newest', 'highest', 'lowest'].map((opt) => {
                  const label = opt === 'most_relevant' ? 'Most relevant' : opt === 'newest' ? 'Newest' : opt === 'highest' ? 'Highest rating' : 'Lowest rating';
                  return <button key={opt} onClick={() => setSortOption(opt)} className={`btn ${sortOption === opt ? 'btn-primary' : 'btn-outline-secondary'}`} style={{ borderRadius: 20, padding: '6px 12px', cursor: 'pointer' }}>{label}</button>;
                })}
              </div>
            </div>

            {showWriteForm && (
              <form onSubmit={handleSubmitReview} style={{ padding: '20px 22px', borderBottom: '1px solid #f7f7f7' }}>
                <div style={{ textAlign: 'center', marginBottom: 12 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>Your Rating:</label>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
                    {[1, 2, 3, 4, 5].map((r) => <i key={r} className={r <= newRating ? 'fas fa-star' : 'far fa-star'} style={{ color: '#f4b400', fontSize: 30, cursor: 'pointer', transform: r <= newRating ? 'scale(1.15)' : 'scale(1)', transition: 'transform 0.12s' }} onClick={() => setNewRating(r)} />)}
                  </div>
                </div>
                <textarea value={newReviewText} onChange={(e) => setNewReviewText(e.target.value)} placeholder="Write your review..." rows={4} style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ddd' }} />
                <div style={{ marginTop: 14, display: 'flex', justifyContent: 'center', gap: 12 }}>
                  <button type="submit" className="btn btn-primary" style={{ minWidth: 120 }}>{isEditing ? 'Update' : 'Post'}</button>
                  <button type="button" className="btn btn-secondary" style={{ minWidth: 120 }} onClick={() => { setShowWriteForm(false); setIsEditing(false); }}>Cancel</button>
                </div>
              </form>
            )}

            <div style={{ padding: '12px 22px 22px 22px' }}>
              {reviewsLoading ? <div>Loading reviews...</div> : reviewsList.length === 0 ? <div style={{ color: '#666', padding: '18px 0' }}>No reviews yet.</div> : (() => {
                const sorted = [...reviewsList];
                if (sortOption === 'newest') sorted.sort((a, b) => new Date(b.created_at || b.createdAt) - new Date(a.created_at || a.createdAt));
                else if (sortOption === 'highest') sorted.sort((a, b) => (b.rating || 0) - (a.rating || 0));
                else if (sortOption === 'lowest') sorted.sort((a, b) => (a.rating || 0) - (b.rating || 0));
                return sorted.map((r) => {
                  const userObj = r.User || usersMap[r.user_id] || r.user || null;
                  const displayName = (userObj && (userObj.name || userObj.phone || userObj.username)) ? (userObj.name || userObj.phone || userObj.username) : 'Guest';
                  const avatarLetter = displayName ? displayName.charAt(0).toUpperCase() : 'G';
                  const createdAt = new Date(r.created_at || r.createdAt);
                  const formattedDate = isNaN(createdAt.getTime()) ? '' : formatDateOnly(createdAt);
                  return (
                    <div key={r.id || `${r.user_id}-${r.created_at}`} style={{ borderBottom: '1px solid #eee', paddingBottom: 12, marginBottom: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 42, height: 42, borderRadius: '50%', background: '#f1f1f1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600 }}>{avatarLetter}</div>
                        <div>
                          <div style={{ fontWeight: 600 }}>{displayName}</div>
                          <div style={{ fontSize: 12, color: '#666' }}>{formattedDate}</div>
                        </div>
                        <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                          <div>{renderStarsHoliday(r.rating, 16)}</div>
                          <div style={{ fontSize: 12, color: '#666' }}>{(r.rating || 0).toFixed(1)} ★</div>
                        </div>
                      </div>
                      {r.review_text && <div style={{ marginTop: 8 }}>{r.review_text}</div>}
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      )}
      {showLoginPopup && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999999 }} onClick={() => { setShowLoginPopup(false); setPendingAction(null); setPendingReviewOpen(false); }}>
          <div onClick={(e) => e.stopPropagation()}>
            <LoginPopup
              visible={showLoginPopup}
              onClose={() => { setShowLoginPopup(false); setPendingAction(null); setPendingReviewOpen(false); }}
              onLoginSuccess={handleLoginSuccess}
            />
          </div>
        </div>
      )}

      {(isSubmittingQuery || showThankYouModal) && !showLoginPopup && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999999 }} onClick={showThankYouModal ? () => setShowThankYouModal(false) : undefined}>
          <div style={{ background: 'white', padding: '40px', borderRadius: '16px', maxWidth: '400px', width: '90%', textAlign: 'center', boxShadow: '0 10px 30px rgba(0,0,0,0.2)', animation: 'slideUp 0.3s ease' }} onClick={(e) => e.stopPropagation()}>
            {isSubmittingQuery && !showThankYouModal ? (
              <>
                <div style={{ marginBottom: '20px' }}>
                  <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem', borderWidth: '0.3em' }}>
                    <span className="visually-hidden">Processing...</span>
                  </div>
                </div>
                <h4 style={{ fontWeight: 'bold', marginBottom: '10px' }}>Processing your request...</h4>
                <p style={{ color: '#6c757d', marginBottom: 0 }}>Please wait while we save your lead and notify the vendor.</p>
              </>
            ) : (
              <>
                <div style={{ marginBottom: '20px' }}>
                  <i className="fas fa-check-circle text-success" style={{ fontSize: '4rem', color: '#198754' }}></i>
                </div>
                <h2 style={{ fontWeight: 'bold', marginBottom: '12px' }}>Thank You!</h2>
                <p style={{ color: '#6c757d', marginBottom: '24px' }}>Your request has been sent to the team. We will get back to you shortly.</p>
                <button className="btn btn-primary" style={{ borderRadius: '50px', padding: '10px 30px', fontWeight: 'bold' }} onClick={() => setShowThankYouModal(false)}>
                  OK
                </button>
              </>
            )}
          </div>
        </div>
      )}
      {/* Mobile Sticky Bottom Bar */}
      <div className={`holiday-mobile-sticky-bar ${showStickyBar ? 'visible' : ''}`}>
        <div className="holiday-sticky-price-info">
          <div className="holiday-sticky-price-row">
            <span className="holiday-sticky-price-current">
              ₹{offerPrice ? Math.round(offerPrice).toLocaleString() : (price ? Math.round(price).toLocaleString() : 'Price on request')}
            </span>
            {offerPrice && price && Number(price) > Number(offerPrice) && (
              <span className="holiday-sticky-price-strike">
                ₹{Math.round(price).toLocaleString()}
              </span>
            )}
          </div>
          {(offerPrice || price) && (
            <div className="holiday-sticky-taxes-label">
              per person
            </div>
          )}
        </div>
        <div className="holiday-sticky-action-container">
          <button
            className="holiday-sticky-book-btn"
            onClick={(e) => handleAction(e, "query")}
            disabled={isSubmittingQuery}
          >
            {isSubmittingQuery && pendingAction === "query" ? (
              <span className="spinner-border spinner-border-sm" style={{ marginRight: '6px', display: 'inline-block', width: '1rem', height: '1rem', verticalAlign: 'text-bottom', border: '.2em solid currentColor', borderRightColor: 'transparent', borderRadius: '50%', animation: 'spinner-border .75s linear infinite' }}></span>
            ) : (
              <>
                <span className="btn-title-main">Enquire Now</span>
              </>
            )}
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default YatraDetails;
